import clsx from 'clsx';
import { uniqueId } from 'lodash-es';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { mergeProps } from 'src/core/utils';

import { hoverEffect, hoverWhiteEffect } from './SvgWrapper.css';

export type PosIconProps = ComponentPropsWithoutRef<'svg'> & {
  size?: string;
  withHoverEffect?: boolean;
  darkModeLightHover?: boolean;
  title?: string;
  disabled?: boolean;
};

type ViewBoxProp = {
  /**
   * Custom viewbox definition should be defined specific to the SVG you're pulling from
   */
  viewBox: string;
};

export const SvgWrapper = forwardRef<SVGSVGElement, PosIconProps & ViewBoxProp>(
  function SvgWrapper(
    {
      withHoverEffect,
      darkModeLightHover,
      size = vars.iconSize.l,
      children,
      height,
      width,
      title,
      fill,
      stroke,
      disabled,
      ...props
    },
    ref
  ) {
    fill = disabled ? vars.color.textDisabled : fill;

    const [parsedWidth, parsedHeight] = [width, height].map((attr) =>
      attr != null ? parseInt(attr.toString(), 10) : attr
    );

    const inheritedWidth = parsedWidth ?? parsedHeight ?? 24;
    const inheritedHeight = parsedHeight ?? parsedWidth ?? 24;
    const newUniqueId = uniqueId();

    const { isDarkMode } = useSiteTheme();

    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        fill={fill || 'currentColor'}
        stroke={stroke || 'inherit'}
        width={inheritedWidth}
        height={inheritedHeight}
        {...mergeProps(
          {
            className: clsx(`custom-sizing-${newUniqueId}`, {
              [hoverEffect]:
                withHoverEffect && !(isDarkMode && darkModeLightHover),
              [hoverWhiteEffect]: isDarkMode && darkModeLightHover,
            }),
            cursor: disabled ? 'none' : undefined,
          },
          props
        )}
      >
        {size && (
          <defs>
            <style type="text/css">{`.custom-sizing-${newUniqueId} {width: ${
              size ?? inheritedWidth
            }; height: ${size ?? inheritedHeight};}`}</style>
          </defs>
        )}
        {children}
        {title && <title>{title}</title>}
      </svg>
    );
  }
);
